import { createContext, useContext, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import useGetCorporations from 'packages/utils/hooks/workDirection/useGetCorporations';
import { db } from 'firebase/firebase';
import { getDoc, doc } from 'firebase/firestore';
import { FSCollections } from '@src/constants/database';
import useCloudFunction from '@src/packages/utils/hooks/clouds/useCloudFunction';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { get } from 'packages/utils/storage';

export const TimeTrackerContext = createContext();

/** This is a provider that will be used to manage the organization data related to Attendance Reports / Time Tracker.
 * @typedef {Object} organizationRef
 * @property {string|null} organization - The organization string name
 * @property {string|null} rut - The RUT (Tax ID) number
 * @property {string|null} rutFormated - The formatted RUT number
 */
export const TimeTrackerProvider = ({ children }) => {
  const { getCorporationById, getOrgByCorporation } = useGetCorporations();
  const org = useSelector(state => state?.organizations?.organization) || {};
  const orgData = org?.data || {};
  const selectedOrgId = org?.id;

  const isFromOperonda = !!selectedOrgId;
  const userId = get(isFromOperonda ? 'user_id' : 'firebaseUID');

  const [corporationSelected, setCorporationSelected] = useState(null);
  const [corporationId, setCorporationId] = useState(null);
  const [organizationIds, setOrganizationIds] = useState([]); // NOTE: these organization belong to each corporation
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [shouldSendEmail, setShouldSendEmail] = useState(false);
  const [reportData, setReportData] = useState({});

  const { mutateAsync: sendFiscalizationReport } = useCloudFunction({
    op: API_OPERATIONS.FISCALIZATION_REPORT,
    params: {
      organizationIds: organizationIds,
    },
    runFirstTime: false,
  });

  const sendFiscalizationReportEmail = () => {
    sendFiscalizationReport({
      userId: userId,
      organizationId: organizationIds[0],
    }).catch(error => {
      console.error('Error sending fiscalization report email', error);
    });
  };

  /**
   * Save the selected corporation data to the context.
   *
   * @param {Object} values - The selected corporation values
   */
  const onSelectCorporation = values => {
    if (!values) return;

    setCorporationSelected({
      id: values?.id || null,
      name: values?.name || null,
      RUT: values?.RUT || null,
    });

    // Send fiscalization report email
    setShouldSendEmail(true);
  };

  const onCollapse = value => {
    setCollapsed(value);
  };

  /**
   * Set the corporationId and organizationIds due to Operonda APP.
   */
  useEffect(() => {
    if (isFromOperonda) {
      setCorporationId(orgData?.corporationId);
      setOrganizationIds([selectedOrgId]);
      setCountry(orgData?.country);
    } else {
      // Default country is Chile, when services allow get organization country using genericFind API we will change this
      setCountry('Chile');
    }
  }, [selectedOrgId]);

  // Fetch organization IDs by the selected corporation
  useEffect(() => {
    // If has org.id we are in Operonda
    if (isFromOperonda) return;

    const fetchOrganizationIds = async () => {
      if (!corporationSelected) return;
      try {
        setLoading(true);
        const { data = [] } = await getOrgByCorporation(corporationSelected.id);
        setOrganizationIds(data);
      } catch {
        setOrganizationIds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationIds();
  }, [corporationSelected]);

  // Fetch corporation data by corporationId to keep the data when the page is refreshed
  useEffect(() => {
    if (Object.values(corporationSelected || {}).length > 0 || !corporationId) return;

    const fetchCorporation = async () => {
      try {
        setLoading(true);
        if (isFromOperonda) {
          try {
            const docRef = doc(db, FSCollections.CORPORATIONS, corporationId);
            const res = await getDoc(docRef);
            const { id = null, name = null, RUT = null } = res.data() || {};
            setCorporationSelected({
              id,
              name,
              RUT,
            });
          } catch (error) {
            return new Error(error);
          }
          return;
        }
        const { data = null } = await getCorporationById(corporationId);
        if (data) {
          setCorporationSelected({
            id: data?.id || null,
            name: data?.name || null,
            RUT: data?.RUT || null,
          });
        }
      } catch (error) {
        setCorporationSelected(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCorporation();
  }, [corporationId]);

  // Manage the email sending - Fiscalization Report
  useEffect(() => {
    if (shouldSendEmail && organizationIds?.length > 0 && userId) {
      sendFiscalizationReportEmail();
      setShouldSendEmail(false);
    }
  }, [organizationIds, shouldSendEmail, userId]);

  return (
    <TimeTrackerContext.Provider
      value={{
        corporation: corporationSelected || null,
        onSelectCorporation,
        setCorporationId,
        organizationIds,
        loading,
        isFromOperonda,
        country,
        collapsed,
        setCollapsed: onCollapse,
        reportData,
        setReportData,
      }}
    >
      {children}
    </TimeTrackerContext.Provider>
  );
};

export const useTimeTracker = () => {
  const context = useContext(TimeTrackerContext);
  if (context === undefined) {
    throw new Error('useTimeTracker must be used within a TimeTrackerProvider');
  }
  return context;
};
